import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, RefreshCw, Loader2, AlertTriangle, DollarSign, Clock, Bell, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import { portalPath } from '../../lib/portal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/finance`;

const AGING_META = [
  { k: 'current', l: 'Current', c: 'emerald' },
  { k: '1_30',    l: '1–30 days', c: 'amber' },
  { k: '31_60',   l: '31–60 days', c: 'orange' },
  { k: '61_90',   l: '61–90 days', c: 'red' },
  { k: 'over_90', l: '90+ days', c: 'rose' },
];
const COLOUR_MAP = {
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  amber:   'bg-amber-100 text-amber-800 border-amber-200',
  orange:  'bg-orange-100 text-orange-800 border-orange-200',
  red:     'bg-red-100 text-red-800 border-red-200',
  rose:    'bg-rose-100 text-rose-800 border-rose-200',
};

const fmt = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const GovFinanceDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard`);
      setData(res.data || {});
    } catch (e) {
      toast({ title: 'Load failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNudge = async () => {
    setNudging(true);
    try {
      const r = await axios.post(`${API}/notifications/run`, {});
      toast({ title: 'Nudges sent', description: `Due-soon ${r.data.sent.due_soon} · Overdue ${r.data.sent.overdue}` });
    } catch (e) {
      toast({ title: 'Nudge failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setNudging(false); }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-12"><Loader2 className="h-10 w-10 animate-spin text-blue-700" /></div>;
  }

  const t = data.totals || {};
  const agingTotal = AGING_META.reduce((s, m) => s + Number(data.aging?.[m.k] || 0), 0);

  return (
    <div className="space-y-4" data-testid="gov-finance-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-blue-700" /> Accounts Receivable
          </h1>
          <p className="text-sm text-slate-500">Real-time collection health. Refreshed {new Date(data.generated_at).toLocaleString()}.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runNudge} disabled={nudging} data-testid="run-nudge-btn">
            {nudging ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Bell className="h-4 w-4 mr-1" />} Send Reminders
          </Button>
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <KPI colour="emerald" icon={DollarSign} label="Amount Paid" value={fmt(t.amount_paid)} />
        <KPI colour="blue"    icon={Clock}      label="Outstanding" value={fmt(t.amount_outstanding)} sub={`${t.invoices_open || 0} open`} />
        <KPI colour="rose"    icon={AlertTriangle} label="Overdue" value={fmt(t.amount_overdue)} sub={`${t.overdue_count || 0} invoices`} />
        <KPI colour="amber"   icon={TrendingUp} label="Today's Sales" value={fmt(data.todays_sales)} sub={`payments ${fmt(data.payments_today)}`} />
      </div>

      {/* Aging */}
      <Card className="border-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Aging Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2 mb-3">
            {AGING_META.map(m => {
              const v = Number(data.aging?.[m.k] || 0);
              const pct = agingTotal > 0 ? Math.round((v / agingTotal) * 100) : 0;
              return (
                <Link key={m.k} to={portalPath(`/invoices?aging=${m.k}`)} className={`block p-3 border-2 rounded-lg ${COLOUR_MAP[m.c]}`} data-testid={`aging-${m.k}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold">{m.l}</div>
                  <div className="text-xl font-bold">{fmt(v)}</div>
                  <div className="text-xs opacity-70">{pct}%</div>
                </Link>
              );
            })}
          </div>
          {/* Stacked bar */}
          <div className="flex h-2 rounded overflow-hidden bg-slate-100">
            {AGING_META.map(m => {
              const v = Number(data.aging?.[m.k] || 0);
              const pct = agingTotal > 0 ? (v / agingTotal) * 100 : 0;
              const barColour = { emerald: '#10b981', amber: '#f59e0b', orange: '#f97316', red: '#ef4444', rose: '#e11d48' }[m.c];
              return <div key={m.k} style={{ width: `${pct}%`, background: barColour }} title={`${m.l}: ${fmt(v)}`} />;
            })}
          </div>
        </CardContent>
      </Card>

      {/* Two columns: top customers + revenue by status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Top Outstanding by Customer</CardTitle></CardHeader>
          <CardContent>
            {(data.top_customers || []).length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">No outstanding balances.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.top_customers.map((c, i) => (
                    <tr key={i} className="border-t first:border-t-0">
                      <td className="p-2">{c.agency}</td>
                      <td className="p-2 text-right font-mono text-rose-700">{fmt(c.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
        <Card className="border-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Revenue by Status</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(data.revenue_by_status || {}).length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">No revenue yet.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(data.revenue_by_status).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                    <tr key={k} className="border-t first:border-t-0">
                      <td className="p-2 uppercase text-xs text-slate-500">{k.replace('_', ' ')}</td>
                      <td className="p-2 text-right font-mono">{fmt(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {(t.invoices_awaiting_approval > 0 || t.invoices_disputed > 0) && (
        <Card className="border-2 border-amber-300 bg-amber-50">
          <CardContent className="p-3 text-sm text-amber-900 flex gap-4 flex-wrap">
            {t.invoices_awaiting_approval > 0 && <span>⏳ <strong>{t.invoices_awaiting_approval}</strong> invoices awaiting your approval.</span>}
            {t.invoices_disputed > 0 && <span>⚠ <strong>{t.invoices_disputed}</strong> invoices in dispute.</span>}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const KPI = ({ colour, icon: Icon, label, value, sub }) => (
  <div className={`p-3 rounded-lg border-2 ${COLOUR_MAP[colour] || COLOUR_MAP.emerald}`}>
    <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</div>
    <div className="text-2xl font-bold">{value}</div>
    {sub && <div className="text-xs opacity-70">{sub}</div>}
  </div>
);

export default GovFinanceDashboard;
